import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useUpdateAssociatedParty,
  type AssociatedPartyDetails,
  type UpdateAssociatedPartyPayload,
} from '@/shared/hooks/api/associatedParties'
import { AssociatedPartyForm } from './AssociatedPartyForm'

type EditAssociatedPartyModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  associatedParty: AssociatedPartyDetails
}

export function EditAssociatedPartyModal({
  open,
  onOpenChange,
  associatedParty,
}: EditAssociatedPartyModalProps) {
  const { t } = useTranslation('associatedParties')
  const { mutate: updateParty, isPending } = useUpdateAssociatedParty(
    associatedParty.id
  )

  const initialValues = useMemo(
    () => ({
      name: associatedParty.name,
      street: associatedParty.street,
      street2: associatedParty.street2,
      streetNumber: associatedParty.streetNumber,
      subDivision: associatedParty.subDivision,
      country: associatedParty.country,
      postCode: associatedParty.postCode,
      poBox: associatedParty.poBox,
      city: associatedParty.city,
      contactName: associatedParty.contactName,
      contactPhoneNumber: associatedParty.contactPhoneNumber,
      contactEmail: associatedParty.contactEmail,
    }),
    [associatedParty]
  )

  const handleSubmit = (payload: UpdateAssociatedPartyPayload) => {
    updateParty(payload, {
      onSuccess: () => {
        toast.success(t('editModal.success'))
        onOpenChange(false)
      },
      onError: (error) => {
        toast.error(error.message || t('editModal.error'))
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[90vh] max-w-4xl! flex-col'>
        <DialogHeader>
          <DialogTitle>{t('editModal.title')}</DialogTitle>
        </DialogHeader>
        <AssociatedPartyForm
          open={open}
          initialValues={initialValues}
          inputIdPrefix='edit-associated-party'
          onSubmit={handleSubmit}
          onCancel={() => onOpenChange(false)}
          submitLabel={t('editModal.submit')}
          cancelLabel={t('addModal.cancel')}
          isPending={isPending}
        />
      </DialogContent>
    </Dialog>
  )
}
